import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Accordion from '@radix-ui/react-accordion';
import Field from 'components/forms/field/Field';
import Select from 'components/forms/select/Select';
import { weekStartsOnOptions, yearStartsOnMonthOptions } from './calendarSettingsFields';

/**
 * Shows a calendar settings accordion with options to set the first day of the week and the first day of the year.
 * For use in the custom timeframe form.
 */
export default function CalendarSettings({
    yearStartMonth,
    className
}: {
    /** What is the currently set first month of year */
    yearStartMonth?: number;
    className?: string;
}) {
    // generate options for year start day field based on the number of days in the selected month
    const yearStartMonthDayCount = yearStartsOnMonthOptions[yearStartMonth ?? 0].days;
    const yearStartDayOptions = Array.from({ length: yearStartMonthDayCount }, (_, i) => {
        const day = i + 1;
        const dayString = day < 10 ? `0${day}` : `${day}`;
        return {
            label: dayString,
            value: day
        };
    });

    return (
        <Accordion.Root type='single' collapsible className={className}>
            <Accordion.Item value='calendar-settings' className='border-t group border-t-dividerPrimary'>
                <Accordion.Header>
                    <Accordion.Trigger
                        className='flex justify-between w-full py-3 font-semibold select-none'
                        data-testid='calendarSettings'
                    >
                        <span>Calendar settings</span>
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className='group-data-[state="open"]:rotate-180 transition-transform duration-300 ease-in-out'
                        />
                    </Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Content className='accordion-content'>
                    <div className='flex flex-wrap items-start pb-4 gap-x-8'>
                        <Field label='First day of week'>
                            <Select name='weekStartsOn' valueAsNumber>
                                <Select.TriggerButton className='w-full min-w-[230px]' aria-label='Week start day'>
                                    <Select.Value placeholder='Weekday' />
                                </Select.TriggerButton>
                                <Select.Content className='z-10'>
                                    {weekStartsOnOptions.map((option) => (
                                        <Select.Item key={option.value} value={option.value.toString()}>
                                            {option.label}
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select>
                        </Field>
                        <Field label='First day of year' className='mt-0'>
                            <div className='flex flex-1 gap-2'>
                                <Select name='yearStartsOn.day' valueAsNumber>
                                    <Select.TriggerButton className='w-full min-w-[80px]' aria-label='Year start day'>
                                        <Select.Value placeholder='Day' />
                                    </Select.TriggerButton>
                                    <Select.Content className='z-10'>
                                        {yearStartDayOptions.map((option) => (
                                            <Select.Item key={option.value} value={option.value.toString()}>
                                                {option.label}
                                            </Select.Item>
                                        ))}
                                    </Select.Content>
                                </Select>
                                <Select name='yearStartsOn.month' valueAsNumber>
                                    <Select.TriggerButton
                                        className='w-full min-w-[140px]'
                                        aria-label='Year start month'
                                    >
                                        <Select.Value placeholder='Month' />
                                    </Select.TriggerButton>
                                    <Select.Content className='z-10'>
                                        {yearStartsOnMonthOptions.map((option) => (
                                            <Select.Item key={option.value} value={option.value.toString()}>
                                                {option.label}
                                            </Select.Item>
                                        ))}
                                    </Select.Content>
                                </Select>
                            </div>
                        </Field>
                    </div>
                </Accordion.Content>
            </Accordion.Item>
        </Accordion.Root>
    );
}
